/**
 * PhotoService
 *
 * Manages photo uploads, metadata, and Firebase Storage integration.
 * Follows the 10-step integration pattern via BaseContentService.
 *
 * Features:
 * - Firebase Storage upload with progress tracking
 * - Batch photo uploads
 * - Person tagging with coordinates
 * - EXIF data extraction
 * - Approval workflow integration
 * - SystemConfig validation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  QueryConstraint,
  serverTimestamp,
} from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTaskSnapshot
} from 'firebase/storage';

import { Photo, SystemConfig } from '../types';
import {
  PhotoPersonTag,
  PhotoProcessingStatus,
  PhotoUsage
} from '../types/photo';

import {
  BaseContentService,
  BaseContentEntity,
  CreateContentOptions,
} from './baseContentService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';
import { PersonService } from './personService';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Photo entity extending base content entity
 */
export interface PhotoEntity extends BaseContentEntity {
  fileName: string;
  originalFileName: string;
  storageURL: string;
  thumbnailURL?: string;
  mediumURL?: string;

  // Photo metadata
  caption?: string;
  description?: string;
  dateTaken?: string;
  photographer?: string;

  // Technical metadata
  fileSize: number;
  mimeType: string;
  dimensions: {
    width: number;
    height: number;
  };

  // Organization
  albumId?: string;
  eventId?: string;
  tags: string[];
  taggedPersons: PhotoPersonTag[];

  // Permissions and status
  isPublic: boolean;

  // Source and attribution
  source?: string;
  copyright?: string;
  usage?: PhotoUsage;

  // Processing status
  processingStatus: PhotoProcessingStatus;
}

/**
 * Photo metadata for updates
 */
export interface PhotoMetadata {
  caption?: string;
  description?: string;
  dateTaken?: string;
  photographer?: string;
  tags?: string[];
  albumId?: string;
  isPublic?: boolean;
  usage?: PhotoUsage;
  source?: string;
  copyright?: string;
}

/**
 * Tag coordinates for person tagging
 */
export interface TagCoordinates {
  x: number; // Percentage from left
  y: number; // Percentage from top
  width: number; // Percentage width
  height: number; // Percentage height
}

/**
 * Basic EXIF data extraction interface
 */
interface ExifData {
  dateTaken?: string;
  camera?: string;
  location?: {
    latitude?: number;
    longitude?: number;
  };
}

/**
 * Photo creation data (before Firebase Storage upload)
 */
interface PhotoCreationData {
  fileName: string;
  originalFileName: string;
  storageURL: string;
  caption?: string;
  description?: string;
  dateTaken: string;
  photographer?: string;
  fileSize: number;
  mimeType: string;
  dimensions: {
    width: number;
    height: number;
  };
  albumId?: string;
  eventId?: string;
  tags: string[];
  taggedPersons: PhotoPersonTag[];
  isPublic: boolean;
  source: string;
  usage: PhotoUsage;
  processingStatus: PhotoProcessingStatus;
  personId: string; // Required for BaseContentService
}

// ============================================================================
// PhotoService Class
// ============================================================================

/**
 * PhotoService - Manages photo uploads and operations
 * Extends BaseContentService for 10-step compliance
 */
class PhotoServiceImpl extends BaseContentService<PhotoEntity> {
  // ============================================================================
  // STATIC CONSTANTS
  // ============================================================================

  private static readonly STORAGE_PATH_PREFIX = 'photos';

  // ============================================================================
  // ABSTRACT PROPERTIES IMPLEMENTATION
  // ============================================================================

  protected collectionName = 'photos';
  protected featureFlagKey: keyof SystemConfig['features'] = 'enablePhotoGallery';
  protected approvalConfigKey: keyof SystemConfig['content'] = 'requirePhotoApproval';
  protected maxPerPersonConfigKey: keyof SystemConfig['content'] = 'maxPhotosPerPerson';
  protected authorFieldName = 'createdBy'; // Photos use createdBy as author field
  protected changeTrackingEntityType = 'photo';

  // ============================================================================
  // ABSTRACT METHODS IMPLEMENTATION
  // ============================================================================

  /**
   * Validate photo-specific data
   */
  protected async validateContent(data: PhotoCreationData, config: SystemConfig): Promise<void> {
    // Validate file size
    const maxSizeMB = config.content.maxPhotoSizeMB || 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (data.fileSize > maxSizeBytes) {
      throw new Error(`Photo exceeds maximum size of ${maxSizeMB}MB`);
    }

    // Validate file type
    const allowedTypes = config.content.allowedPhotoTypes || ['jpg', 'jpeg', 'png', 'gif'];
    const fileExtension = data.originalFileName.split('.').pop()?.toLowerCase() || '';

    if (!allowedTypes.includes(fileExtension)) {
      throw new Error(`Photo type .${fileExtension} not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }

    // Validate required fields
    if (!data.fileName || !data.storageURL) {
      throw new Error('fileName and storageURL are required');
    }

    if (!data.personId) {
      throw new Error('personId is required for photo upload');
    }
  }

  /**
   * Build Firestore query constraints from filters
   */
  protected buildFilters(filters: any): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];

    if (filters.isPublic !== undefined) {
      constraints.push(where('isPublic', '==', filters.isPublic));
    }

    if (filters.albumId) {
      constraints.push(where('albumId', '==', filters.albumId));
    }

    if (filters.photographer) {
      constraints.push(where('photographer', '==', filters.photographer));
    }

    return constraints;
  }

  /**
   * Map Firestore document data to typed entity
   */
  protected mapDocumentToEntity(docData: any, docId: string): PhotoEntity {
    return {
      id: docId,
      personId: docData.personId || docData.createdBy, // Fallback for old data
      fileName: docData.fileName,
      originalFileName: docData.originalFileName,
      storageURL: docData.storageURL,
      thumbnailURL: docData.thumbnailURL,
      mediumURL: docData.mediumURL,
      caption: docData.caption,
      description: docData.description,
      dateTaken: docData.dateTaken,
      photographer: docData.photographer,
      fileSize: docData.fileSize,
      mimeType: docData.mimeType,
      dimensions: docData.dimensions,
      albumId: docData.albumId,
      eventId: docData.eventId,
      tags: docData.tags || [],
      taggedPersons: docData.taggedPersons || [],
      isPublic: docData.isPublic,
      source: docData.source,
      copyright: docData.copyright,
      usage: docData.usage,
      processingStatus: docData.processingStatus,
      createdAt: this.timestampToDate(docData.createdAt),
      updatedAt: this.timestampToDate(docData.updatedAt),
      createdBy: docData.createdBy,
      isApproved: docData.isApproved,
      isDeleted: docData.isDeleted,
    };
  }

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Upload multiple photos to Firebase Storage and create Firestore documents
   * Follows the 10-step integration pattern for each photo
   *
   * @param files Array of File objects to upload
   * @param personId ID of the person uploading (owner)
   * @param userId User ID performing the upload
   * @param userName User name performing the upload
   * @param userRole User role (for permissions checking)
   * @param onProgress Optional callback for upload progress tracking
   * @returns Promise<Photo[]> Array of created Photo documents
   */
  async uploadPhotos(
    files: File[],
    personId: string,
    userId: string,
    userName: string,
    userRole: string,
    onProgress?: (snapshot: UploadTaskSnapshot) => void
  ): Promise<Photo[]> {
    if (!files || files.length === 0) {
      throw new Error('No files provided for upload');
    }

    if (!personId) {
      throw new Error('Person ID is required for photo upload');
    }

    const uploadedPhotos: Photo[] = [];
    const errors: Error[] = [];

    for (const file of files) {
      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`File ${file.name} is not an image`);
        }

        // Generate unique filename
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2);
        const fileExtension = file.name.split('.').pop() || 'jpg';
        const fileName = `${timestamp}_${randomId}.${fileExtension}`;

        // Create storage reference
        const storagePath = `${PhotoServiceImpl.STORAGE_PATH_PREFIX}/${personId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        // Extract basic file metadata
        const dimensions = await this.getImageDimensions(file);
        const exifData = await this.extractBasicExifData(file);

        // Upload file with progress tracking
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Set up progress tracking
        if (onProgress) {
          uploadTask.on('state_changed', onProgress);
        }

        // Wait for upload completion
        const snapshot = await uploadTask;
        const downloadURL = await getDownloadURL(snapshot.ref);

        // Get person name for tagging
        let personName = 'Unknown';
        try {
          const person = await PersonService.getPersonById(personId);
          if (person) {
            personName = `${person.firstName} ${person.lastName}`.trim();
          }
        } catch (err) {
          console.warn('Could not fetch person name for tag:', err);
        }

        // Prepare photo data with automatic person tagging
        const photoData: PhotoCreationData = {
          personId,
          fileName,
          originalFileName: file.name,
          storageURL: downloadURL,
          caption: '',
          description: '',
          dateTaken: exifData.dateTaken || new Date().toISOString(),
          photographer: '',
          fileSize: file.size,
          mimeType: file.type,
          dimensions,
          albumId: '',
          eventId: '',
          tags: [],
          // Auto-tag the person when uploading to their profile
          taggedPersons: [{
            personId,
            personName,
            confidence: 100, // 100% confidence for manual uploads
            confirmedBy: userId
          }],
          isPublic: false,
          source: 'upload',
          usage: 'family-only',
          processingStatus: 'ready',
        };

        // Execute 10-step pattern via BaseContentService
        const createdEntity = await this.executeCreatePattern(
          photoData,
          userId,
          userName,
          userRole,
          {
            requiresApproval: true, // Will be determined by config
            entityTypeName: 'photo',
            skipNotification: false,
          }
        );

        // Convert to Photo type for return
        const photo: Photo = {
          ...createdEntity,
          // Map PhotoEntity fields to Photo type (they're compatible)
        } as unknown as Photo;

        uploadedPhotos.push(photo);

      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        errors.push(error instanceof Error ? error : new Error(`Failed to upload ${file.name}`));
      }
    }

    // If all uploads failed, throw error
    if (uploadedPhotos.length === 0 && errors.length > 0) {
      throw new Error(`All uploads failed: ${errors.map(e => e.message).join(', ')}`);
    }

    // Log partial failures
    if (errors.length > 0) {
      console.warn(`${errors.length} out of ${files.length} uploads failed:`, errors);
    }

    return uploadedPhotos;
  }

  /**
   * Delete a photo from both Firebase Storage and Firestore
   *
   * @param photoId Firestore document ID of the photo
   * @param userId User ID performing the deletion
   * @param userName User name performing the deletion
   * @param userRole User role (for permissions checking)
   * @returns Promise<void>
   */
  async deletePhoto(
    photoId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    if (!photoId) {
      throw new Error('Photo ID is required for deletion');
    }

    try {
      // Get photo document to find the storage path
      const photoDoc = await getDoc(doc(db, this.collectionName, photoId));

      if (!photoDoc.exists()) {
        throw new Error(`Photo with ID ${photoId} not found`);
      }

      const photoData = this.mapDocumentToEntity(photoDoc.data(), photoDoc.id);

      // Check ownership or admin
      if (!this.checkOwnerOrAdmin(photoData, userId, userRole)) {
        throw new Error('You do not have permission to delete this photo');
      }

      // Delete from Firebase Storage
      const storagePath = `${PhotoServiceImpl.STORAGE_PATH_PREFIX}/${photoData.personId}/${photoData.fileName}`;
      const storageRef = ref(storage, storagePath);

      try {
        await deleteObject(storageRef);
      } catch (storageError) {
        console.warn('Failed to delete from storage (file may not exist):', storageError);
        // Continue with Firestore soft deletion even if storage deletion fails
      }

      // Use BaseContentService soft delete (handles change tracking and audit logging)
      await this.softDelete(photoId, userId, userName, userRole);

    } catch (error) {
      console.error('Error deleting photo:', error);
      throw error instanceof Error ? error : new Error('Failed to delete photo');
    }
  }

  /**
   * Update photo metadata in Firestore
   *
   * @param photoId Firestore document ID of the photo
   * @param metadata Partial metadata to update
   * @param userId User ID performing the update
   * @param userName User name performing the update
   * @param userRole User role (for permissions checking)
   * @returns Promise<Photo> Updated Photo document
   */
  async updatePhotoMetadata(
    photoId: string,
    metadata: Partial<PhotoMetadata>,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<Photo> {
    if (!photoId) {
      throw new Error('Photo ID is required for metadata update');
    }

    try {
      const photoRef = doc(db, this.collectionName, photoId);

      // Get current state
      const currentDoc = await getDoc(photoRef);
      if (!currentDoc.exists()) {
        throw new Error(`Photo with ID ${photoId} not found`);
      }

      const beforeState = this.mapDocumentToEntity(currentDoc.data(), currentDoc.id);

      // Check ownership or admin
      if (!this.checkOwnerOrAdmin(beforeState, userId, userRole)) {
        throw new Error('You do not have permission to update this photo');
      }

      // Prepare update data
      const updateData = {
        ...metadata,
        updatedAt: serverTimestamp()
      };

      // Update Firestore document
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(photoRef, updateData);

      // Fetch updated document
      const updatedDoc = await getDoc(photoRef);
      if (!updatedDoc.exists()) {
        throw new Error(`Photo with ID ${photoId} not found after update`);
      }

      const afterState = this.mapDocumentToEntity(updatedDoc.data(), updatedDoc.id);

      // Log to ChangeTrackingService
      await ChangeTrackingService.logChange(
        {
          entityType: 'photo',
          entityId: photoId,
          changeType: 'update',
          beforeState: beforeState,
          afterState: afterState,
        },
        userId,
        userName,
        true
      );

      // Log to AuditLogService
      await AuditLogService.logAction({
        userId,
        action: 'content_approved',
        contentType: 'photo',
        contentId: photoId,
        details: {
          operation: 'update',
          userName,
          personId: afterState.personId,
          updatedFields: Object.keys(metadata),
        },
      });

      return afterState as unknown as Photo;

    } catch (error) {
      console.error('Error updating photo metadata:', error);
      throw error instanceof Error ? error : new Error('Failed to update photo metadata');
    }
  }

  /**
   * Tag a person in a photo with coordinates
   *
   * @param photoId Firestore document ID of the photo
   * @param personId ID of the person to tag
   * @param personName Name of the person to tag
   * @param coordinates Position and size of the tag
   * @param userId User ID performing the tagging
   * @param userName User name performing the tagging
   * @param userRole User role (for permissions checking)
   * @returns Promise<Photo> Updated Photo document
   */
  async tagPersonInPhoto(
    photoId: string,
    personId: string,
    personName: string,
    coordinates: TagCoordinates,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<Photo> {
    if (!photoId || !personId) {
      throw new Error('Photo ID and Person ID are required for tagging');
    }

    // Validate coordinates (must be percentages 0-100)
    if (
      coordinates.x < 0 || coordinates.x > 100 ||
      coordinates.y < 0 || coordinates.y > 100 ||
      coordinates.width < 0 || coordinates.width > 100 ||
      coordinates.height < 0 || coordinates.height > 100
    ) {
      throw new Error('Coordinates must be percentages between 0 and 100');
    }

    try {
      const photoRef = doc(db, this.collectionName, photoId);
      const photoDoc = await getDoc(photoRef);

      if (!photoDoc.exists()) {
        throw new Error(`Photo with ID ${photoId} not found`);
      }

      const photoData = this.mapDocumentToEntity(photoDoc.data(), photoDoc.id);

      // Check ownership or admin
      if (!this.checkOwnerOrAdmin(photoData, userId, userRole)) {
        throw new Error('You do not have permission to tag people in this photo');
      }

      // Check if person is already tagged
      const existingTagIndex = photoData.taggedPersons.findIndex(
        (tag: PhotoPersonTag) => tag.personId === personId
      );

      if (existingTagIndex >= 0) {
        throw new Error('Person is already tagged in this photo');
      }

      const newTag: PhotoPersonTag = {
        personId,
        personName,
        coordinates,
        confirmedAt: new Date(),
        confirmedBy: userId
      };

      // Add new tag
      const updatedTaggedPersons = [...photoData.taggedPersons, newTag];

      // Update document
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(photoRef, {
        taggedPersons: updatedTaggedPersons,
        updatedAt: serverTimestamp()
      });

      // Return updated document
      const updatedDoc = await getDoc(photoRef);
      const afterState = this.mapDocumentToEntity(updatedDoc.data(), updatedDoc.id);

      // Log to ChangeTrackingService
      await ChangeTrackingService.logChange(
        {
          entityType: 'photo',
          entityId: photoId,
          changeType: 'update',
          beforeState: photoData,
          afterState: afterState,
        },
        userId,
        userName,
        true
      );

      // Log to AuditLogService
      await AuditLogService.logAction({
        userId,
        action: 'content_approved',
        contentType: 'photo',
        contentId: photoId,
        details: {
          operation: 'tag_person',
          userName,
          personId: afterState.personId,
          taggedPersonId: personId,
        },
      });

      return afterState as unknown as Photo;

    } catch (error) {
      console.error('Error tagging person in photo:', error);
      throw error instanceof Error ? error : new Error('Failed to tag person in photo');
    }
  }

  /**
   * Retrieve all photos associated with a person
   *
   * @param personId ID of the person
   * @returns Promise<Photo[]> Array of photos
   */
  async getPhotosForPerson(personId: string): Promise<Photo[]> {
    if (!personId) {
      throw new Error('Person ID is required to retrieve photos');
    }

    try {
      // Get all photos and filter client-side for this person
      // Note: We can't query directly on taggedPersons array, so we fetch all
      // and filter in memory. For better performance at scale, consider:
      // 1. Denormalizing personIds into a separate field
      // 2. Using array-contains query (if we store just IDs)
      const allPhotosQuery = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(allPhotosQuery);

      // Filter photos where person is tagged OR person uploaded to their own profile
      const photos: Photo[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();

        // Skip deleted photos
        if (data.isDeleted === true) {
          return;
        }

        const entity = this.mapDocumentToEntity(data, doc.id);

        // Check if person is tagged in the photo
        const isTagged = entity.taggedPersons?.some(
          (tag: PhotoPersonTag) => tag.personId === personId
        );

        // Include if person is tagged OR if uploaded to this person's profile
        if (isTagged || entity.personId === personId) {
          photos.push(entity as unknown as Photo);
        }
      });

      return photos.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

    } catch (error) {
      console.error('Error retrieving photos for person:', error);
      throw new Error('Failed to retrieve photos');
    }
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Extract image dimensions from a File object
   */
  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for dimension extraction'));
      };

      img.src = url;
    });
  }

  /**
   * Extract basic EXIF data from image file
   *
   * Note: This is a basic implementation. In production, consider using
   * a library like 'exifr' or 'piexifjs' for comprehensive EXIF extraction
   */
  private async extractBasicExifData(file: File): Promise<ExifData> {
    const exifData: ExifData = {};

    try {
      // Use file's lastModified as fallback for dateTaken
      if (file.lastModified) {
        exifData.dateTaken = new Date(file.lastModified).toISOString();
      }

      // TODO: Implement proper EXIF extraction using a library like 'exifr'
      // This would extract: DateTimeOriginal, Camera make/model, GPS coordinates, etc.

    } catch (error) {
      console.warn('Failed to extract EXIF data:', error);
    }

    return exifData;
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Singleton instance of PhotoService
 */
const PhotoService = new PhotoServiceImpl();

export default PhotoService;
